/**
 *  @class
 *  @function MobileDock
 */
if (!customElements.get('mobile-dock')) {
  class MobileDock extends HTMLElement {

    connectedCallback() {
      this.bar = this.querySelector('.mobile-dock__bar');
      this.count = this.querySelector('.mobile-dock__count');

      this._onClick = this.onClick.bind(this);
      this.addEventListener('click', this._onClick);

      this.observeHeight();
      this.observeCount();

      if (this.dataset.hideOnScroll === 'true') {
        this.lastY = window.scrollY;
        // A dock moved in the editor keeps its class across reconnects.
        this.visible = this.classList.contains('mobile-dock--visible');
        this._onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this._onScroll, { passive: true });
      }

      if (window.Shopify && Shopify.designMode) {
        this._onEditorSelect = this.onEditorSelect.bind(this);
        this._onSectionLoad = this.observeCount.bind(this);
        document.addEventListener('shopify:section:select', this._onEditorSelect);
        this.addEventListener('shopify:block:select', this._onEditorSelect);
        document.addEventListener('shopify:section:load', this._onSectionLoad);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      if (this._onScroll) {
        window.removeEventListener('scroll', this._onScroll);
      }
      if (this._onEditorSelect) {
        document.removeEventListener('shopify:section:select', this._onEditorSelect);
        this.removeEventListener('shopify:block:select', this._onEditorSelect);
        document.removeEventListener('shopify:section:load', this._onSectionLoad);
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      if (this.countObserver) {
        this.countObserver.disconnect();
      }
      if (this.frame) {
        cancelAnimationFrame(this.frame);
      }
      document.documentElement.style.removeProperty('--mobile-dock-height');
    }

    // Items press the header's own controls, looked up at click time, so the
    // dock and the header can never behave differently. Without the control,
    // the item's own link navigates.
    onClick(event) {
      const item = event.target.closest('[data-dock-action]');
      if (!item) {
        return;
      }
      switch (item.dataset.dockAction) {
        case 'menu':
          this.openMenu();
          break;
        case 'search':
          this.press(event, document.getElementById('quick-search'));
          break;
        case 'cart':
          this.press(event, document.getElementById('cart-drawer-toggle'));
          break;
      }
    }

    press(event, control) {
      if (!control) {
        return;
      }
      event.preventDefault();
      control.click();
    }

    // The header is always sticky, so the menu always opens in view.
    openMenu() {
      const details = document.querySelector('#header .mobile-toggle-wrapper');
      const summary = details && details.querySelector('.mobile-toggle');
      if (!summary) {
        return;
      }
      // header.js drops .active at once but [open] 400ms later; a press in
      // between would leave the menu shut and the page locked.
      if (details.open && !details.classList.contains('active')) {
        return;
      }
      summary.click();
      // The menu covers this button; keep keyboard focus with the menu.
      summary.focus({ preventScroll: true });
    }

    // Publishes the measured height (safe-area inset included) over the
    // section's estimate. Hidden from 768px, it measures 0 and leaves it be.
    observeHeight() {
      if (!this.bar || typeof ResizeObserver === 'undefined') {
        return;
      }
      this.resizeObserver = new ResizeObserver(() => {
        const height = Math.ceil(this.getBoundingClientRect().height);
        if (height > 0) {
          document.documentElement.style.setProperty('--mobile-dock-height', `${height}px`);
        }
      });
      this.resizeObserver.observe(this.bar, { box: 'border-box' });
    }

    // Cart updates only redraw the header's bubble, so mirror it.
    observeCount() {
      if (this.countObserver) {
        this.countObserver.disconnect();
      }
      const toggle = document.getElementById('cart-drawer-toggle');
      if (!this.count || !toggle) {
        return;
      }
      const sync = () => {
        const source = toggle.querySelector('.thb-item-count');
        if (source && this.count.innerHTML !== source.innerHTML) {
          this.count.innerHTML = source.innerHTML;
        }
      };
      this.countObserver = new MutationObserver(sync);
      this.countObserver.observe(toggle, { childList: true, characterData: true, subtree: true });
      sync();
    }

    onScroll() {
      if (this.frame) {
        return;
      }
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.checkVisible();
      });
    }

    // Hidden near the top of the page, shown while scrolling down, hidden
    // again while scrolling up.
    checkVisible() {
      // A locked page (the menu, a drawer or a pop-up) is not the visitor scrolling.
      const body = document.body.classList;
      if (body.contains('open-cc') || body.contains('overflow-hidden')) {
        return;
      }
      const y = Math.max(0, window.scrollY);
      if (y <= 60) {
        this.lastY = y;
        this.setVisible(false);
        return;
      }
      const delta = y - this.lastY;
      if (Math.abs(delta) < 4) {
        return;
      }
      this.lastY = y;
      this.setVisible(delta > 0);
    }

    setVisible(visible) {
      if (this.visible === visible) {
        return;
      }
      this.visible = visible;
      this.classList.toggle('mobile-dock--visible', visible);
    }

    // Selecting the section or one of its items in the editor shows the dock.
    onEditorSelect(event) {
      if (event.type === 'shopify:section:select' && event.detail.sectionId !== this.dataset.sectionId) {
        return;
      }
      if (this.dataset.hideOnScroll === 'true') {
        this.setVisible(true);
      }
    }
  }
  customElements.define('mobile-dock', MobileDock);
}
